/**
 * Error Messages
 *
 * The single place the frontend turns "something failed" into "here is what to tell the
 * user". Use it from services, hooks, pages, mutation `onError` handlers, and error
 * boundaries — there is no second error module.
 *
 * Two entry points over one implementation:
 *
 * ```ts
 * // getErrorMessage — adapts to whatever you hand it, always returns a string.
 * getErrorMessage();                              // generic unknown-error message
 * getErrorMessage(ErrorCode.TOKEN_EXPIRED);       // known code  -> localized message
 * getErrorMessage(t("billing.insufficient"));     // static text -> returned as-is
 * getErrorMessage(error, { fallback: t("errors:api.serverError") });  // parsed
 * getErrorMessage(error, { toast: true });        // parse + notify, ignore the return
 *
 * // resolveError — same engine, structured result when a message is not enough.
 * const failure = resolveError(error);
 * failure.fields;   // { email: "Email already exists" } for form binding
 * failure.status;   // 422
 * failure.isAuth;   // status/code guards, no separate helpers needed
 * ```
 *
 * Messages are never hardcoded here: every code and status resolves through i18next.
 */

import { toast } from "sonner";
import i18n from "@/config/i18n";
import { apiErrorToastId } from "@/lib/apiErrorToast";

/**
 * Error code constants
 */
export const ErrorCode = {
  // Authentication errors (1000-1099)
  INVALID_CREDENTIALS: "AUTH_1001",
  TOKEN_EXPIRED: "AUTH_1002",
  TOKEN_INVALID: "AUTH_1003",
  REFRESH_TOKEN_EXPIRED: "AUTH_1004",
  EMAIL_NOT_VERIFIED: "AUTH_1005",
  ACCOUNT_LOCKED: "AUTH_1006",
  ACCOUNT_SUSPENDED: "AUTH_1007",
  PASSWORD_RESET_REQUIRED: "AUTH_1008",

  // Authorization errors (1100-1199)
  PERMISSION_DENIED: "AUTHZ_1101",
  INSUFFICIENT_PERMISSIONS: "AUTHZ_1102",
  TENANT_ACCESS_DENIED: "AUTHZ_1103",
  RESOURCE_ACCESS_DENIED: "AUTHZ_1104",

  // Validation errors (1200-1299)
  VALIDATION_FAILED: "VAL_1201",
  INVALID_EMAIL: "VAL_1202",
  INVALID_PASSWORD: "VAL_1203",
  PASSWORD_TOO_WEAK: "VAL_1204",
  PASSWORDS_DO_NOT_MATCH: "VAL_1205",
  REQUIRED_FIELD_MISSING: "VAL_1206",
  INVALID_FORMAT: "VAL_1207",

  // Resource errors (1300-1399)
  RESOURCE_NOT_FOUND: "RES_1301",
  RESOURCE_ALREADY_EXISTS: "RES_1302",
  RESOURCE_IN_USE: "RES_1303",
  RESOURCE_LIMIT_REACHED: "RES_1304",
  RESOURCE_UNAVAILABLE: "RES_1305",
  INVALID_RESOURCE_STATE: "RES_1306",
  INVALID_STATUS: "RES_1307",
  RESOURCE_EXPIRED: "RES_1308",

  // Billing errors (1400-1499)
  INSUFFICIENT_BALANCE: "BILL_1401",
  PAYMENT_FAILED: "BILL_1402",
  INVOICE_NOT_FOUND: "BILL_1403",
  PAYMENT_METHOD_REQUIRED: "BILL_1404",
  SUBSCRIPTION_EXPIRED: "BILL_1405",
  CREDIT_LIMIT_EXCEEDED: "BILL_1406",
  CURRENCY_MISMATCH: "BILL_1407",

  // Network errors (1500-1599)
  NETWORK_ERROR: "NET_1501",
  REQUEST_TIMEOUT: "NET_1502",
  CONNECTION_FAILED: "NET_1503",

  // Server errors (1600-1699)
  INTERNAL_SERVER_ERROR: "SRV_1601",
  SERVICE_UNAVAILABLE: "SRV_1602",
  DATABASE_ERROR: "SRV_1603",
  EXTERNAL_SERVICE_ERROR: "SRV_1604",

  // Rate limiting (1700-1799)
  RATE_LIMIT_EXCEEDED: "RATE_1701",
  TOO_MANY_REQUESTS: "RATE_1702",

  // Generic errors
  UNKNOWN_ERROR: "ERR_9999",
} as const;

/**
 * Error code to i18next key. Module-level on purpose — building this map inside the
 * lookup allocated a fresh object on every single error resolved.
 */
const ERROR_CODE_TRANSLATION_KEYS: Record<string, string> = {
  // Authentication errors
  [ErrorCode.INVALID_CREDENTIALS]: "errors:auth.invalidCredentials",
  [ErrorCode.TOKEN_EXPIRED]: "errors:auth.sessionExpired",
  [ErrorCode.TOKEN_INVALID]: "errors:auth.invalidToken",
  [ErrorCode.REFRESH_TOKEN_EXPIRED]: "errors:auth.sessionExpired",
  [ErrorCode.EMAIL_NOT_VERIFIED]: "errors:auth.accountNotVerified",
  [ErrorCode.ACCOUNT_LOCKED]: "errors:auth.accountLocked",
  [ErrorCode.ACCOUNT_SUSPENDED]: "errors:auth.accountLocked",
  [ErrorCode.PASSWORD_RESET_REQUIRED]: "errors:auth.passwordResetFailed",

  // Authorization errors
  [ErrorCode.PERMISSION_DENIED]: "errors:api.forbidden",
  [ErrorCode.INSUFFICIENT_PERMISSIONS]: "errors:api.forbidden",
  [ErrorCode.TENANT_ACCESS_DENIED]: "errors:api.forbidden",
  [ErrorCode.RESOURCE_ACCESS_DENIED]: "errors:api.forbidden",

  // Validation errors
  [ErrorCode.VALIDATION_FAILED]: "errors:api.validation",
  [ErrorCode.INVALID_EMAIL]: "errors:validation.invalidFormat",
  [ErrorCode.INVALID_PASSWORD]: "errors:validation.invalidFormat",
  [ErrorCode.PASSWORD_TOO_WEAK]: "validation:password.minLength",
  [ErrorCode.PASSWORDS_DO_NOT_MATCH]: "validation:password.mismatch",
  [ErrorCode.REQUIRED_FIELD_MISSING]: "errors:validation.required",
  [ErrorCode.INVALID_FORMAT]: "errors:validation.invalidFormat",

  // Resource errors
  [ErrorCode.RESOURCE_NOT_FOUND]: "errors:api.notFound",
  [ErrorCode.RESOURCE_ALREADY_EXISTS]: "errors:api.conflict",
  [ErrorCode.RESOURCE_IN_USE]: "errors:api.conflict",
  [ErrorCode.RESOURCE_LIMIT_REACHED]: "errors:resource.quotaExceeded",
  [ErrorCode.RESOURCE_UNAVAILABLE]: "errors:api.serverError",
  [ErrorCode.INVALID_RESOURCE_STATE]: "errors:api.conflict",
  [ErrorCode.INVALID_STATUS]: "errors:resource.invalidStatus",
  [ErrorCode.RESOURCE_EXPIRED]: "errors:resource.expired",

  // Billing errors
  [ErrorCode.INSUFFICIENT_BALANCE]: "errors:resource.insufficientBalance",
  [ErrorCode.PAYMENT_FAILED]: "errors:api.serverError",
  [ErrorCode.INVOICE_NOT_FOUND]: "errors:api.notFound",
  [ErrorCode.PAYMENT_METHOD_REQUIRED]: "errors:validation.required",
  [ErrorCode.SUBSCRIPTION_EXPIRED]: "errors:api.forbidden",
  [ErrorCode.CREDIT_LIMIT_EXCEEDED]: "errors:resource.quotaExceeded",
  [ErrorCode.CURRENCY_MISMATCH]: "errors:resource.currencyMismatch",

  // Network errors
  [ErrorCode.NETWORK_ERROR]: "errors:api.networkError",
  [ErrorCode.REQUEST_TIMEOUT]: "errors:api.timeout",
  [ErrorCode.CONNECTION_FAILED]: "errors:api.networkError",

  // Server errors
  [ErrorCode.INTERNAL_SERVER_ERROR]: "errors:api.serverError",
  [ErrorCode.SERVICE_UNAVAILABLE]: "errors:api.serverError",
  [ErrorCode.DATABASE_ERROR]: "errors:api.serverError",
  [ErrorCode.EXTERNAL_SERVICE_ERROR]: "errors:api.serverError",

  // Rate limiting
  [ErrorCode.RATE_LIMIT_EXCEEDED]: "errors:api.rateLimited",
  [ErrorCode.TOO_MANY_REQUESTS]: "errors:api.rateLimited",
};

/**
 * The API also returns semantic codes rather than the numbered `ErrorCode` values.
 * They only matter as a fallback: a response that carries its own message always wins,
 * so these keep an unlabelled failure from degrading to the generic server error.
 */
const ERROR_CODE_ALIASES: Record<string, string> = {
  INSUFFICIENT_BALANCE: ErrorCode.INSUFFICIENT_BALANCE,
  // Raised by PayOrderWithBalanceCommandHandler when the tenant's balance is held in a
  // different currency than the order. The backend sentence names both currencies but is
  // English-only, so the localized message wins here (see the alias precedence below).
  CURRENCY_MISMATCH: ErrorCode.CURRENCY_MISMATCH,
  INVALID_STATUS: ErrorCode.INVALID_STATUS,
  // Raised by Start/Stop/Shutdown/Restart/Reset/Resume/UpdateResourceSpecs/RollbackSnapshot
  // command handlers when resource.IsExpired(). Backend message is English-only, so the
  // localized message wins here too (same reasoning as CURRENCY_MISMATCH above).
  RESOURCE_EXPIRED: ErrorCode.RESOURCE_EXPIRED,
  PAYMENT_NOT_FOUND: ErrorCode.INVOICE_NOT_FOUND,
  PAYMENT_FAILED: ErrorCode.PAYMENT_FAILED,
  PAYMENT_CREATION_FAILED: ErrorCode.PAYMENT_FAILED,
  VERIFICATION_FAILED: ErrorCode.PAYMENT_FAILED,
  INVALID_PAYMENT_METHOD: ErrorCode.PAYMENT_METHOD_REQUIRED,
  ORDER_NOT_FOUND: ErrorCode.RESOURCE_NOT_FOUND,
  PAYMENT_ALREADY_VERIFIED: ErrorCode.INVALID_RESOURCE_STATE,
  INVALID_AMOUNT: ErrorCode.INVALID_FORMAT,
  INVALID_FILE: ErrorCode.VALIDATION_FAILED,
  INVALID_FILE_TYPE: ErrorCode.INVALID_FORMAT,
  FILE_TOO_LARGE: ErrorCode.VALIDATION_FAILED,
  UPLOAD_FAILED: ErrorCode.EXTERNAL_SERVICE_ERROR,
};

/**
 * VM-screen codes the Client API returns, mapped straight to an i18n key.
 *
 * Separate from {@link ERROR_CODE_ALIASES} because these have no numbered `ErrorCode`
 * equivalent — there is no generic slot for "snapshot rollback failed" — and inventing
 * one per code would say nothing the key does not already say.
 *
 * A code listed here wins over the sentence the backend sent, which is hardcoded English
 * in the handlers. That trade is deliberate: several of those sentences carry a detail the
 * key cannot repeat (the current status, the exact quota, the disk device), but the screen
 * already shows that detail next to the toast, whereas an English sentence is unreadable
 * to a user running the console in Vietnamese.
 *
 * Codes whose whole value IS the data they carry — VALIDATION_ERROR (per-field messages),
 * INSUFFICIENT_BALANCE (amounts) — are intentionally absent, so their message passes
 * through untouched.
 */
export const VM_ERROR_TRANSLATION_KEYS: Record<string, string> = {
  // Refused by CreatePaymentOrderCommandHandler when the order already holds a live payment.
  // Localized rather than passed through: the backend sentence names the WIC-PAY reference, but
  // it is hardcoded English and the customer has just been shown that reference by the dialog
  // that sent them here.
  ORDER_PAYMENT_ALREADY_OPEN: "errors:payment.orderPaymentAlreadyOpen",

  // Split off from ORDER_PAYMENT_ALREADY_OPEN rather than sharing it, because the shared wording
  // offers "complete that payment, or cancel it" and completing is the exact harm the backend
  // check prevents: the open row quotes a superseded total, so paying it transfers the wrong sum.
  // This wording offers cancelling only.
  ORDER_PAYMENT_STALE_AMOUNT: "errors:payment.orderPaymentStaleAmount",

  // The balance path's own refusal when the order already carries a SETTLED payment. Split off
  // from ORDER_ALREADY_PAID, which the create path raises and which is deliberately passed
  // through untranslated (see hasLocalizedWording) — sharing it would have left this one refusal
  // in English while its sibling ORDER_PAYMENT_ALREADY_OPEN rendered Vietnamese.
  ORDER_PAYMENT_ALREADY_SETTLED: "errors:payment.orderPaymentAlreadySettled",

  // Catch-alls on the two payment routes whose backend sentences are generic English with nothing
  // specific to keep: the balance route's unnamed failure (a lost concurrency race or a server
  // error, both worth retrying), the fraud screen's refusal, and a bank transfer with no account set.
  PAY_ORDER_FAILED: "errors:payment.payOrderFailed",
  PAYMENT_BLOCKED: "errors:payment.paymentBlocked",
  BANK_TRANSFER_NOT_CONFIGURED: "errors:payment.bankTransferNotConfigured",

  // The QR route's refusals. The backend sentences are written for an operator ("Set a Config ID
  // or connect a bank"); the customer only needs to know QR cannot be used and what to pick instead.
  GATEWAY_ERROR: "errors:payment.qrGatewayUnavailable",
  TPAYGATE_NOT_CONFIGURED: "errors:payment.qrNotAvailable",
  TPAYGATE_NO_BANK: "errors:payment.qrNotAvailable",
  QR_CURRENCY_NOT_SUPPORTED: "errors:payment.qrCurrencyNotSupported",

  // Refused when the order being settled is itself a wallet top-up: funding the balance FROM the
  // balance would take the money out and put none back. Localized because the customer reaches it
  // by an ordinary route — the order screen offers every method the same way — and the backend
  // sentence is hardcoded English.
  TOPUP_NOT_PAYABLE_FROM_BALANCE: "errors:payment.topUpNotPayableFromBalance",

  // A tenant may have only ONE payment awaiting confirmation at a time, across every order and
  // top-up. Localized rather than passed through, and deliberately WITHOUT the WIC-PAY reference
  // the backend sentence carries: the blocking payment usually belongs to a different order than
  // the one the customer is looking at, so a bare reference would not tell them where to go. The
  // Vietnamese sentence sends them to the payments screen, where every open one is listed.
  TENANT_PAYMENT_ALREADY_OPEN: "errors:payment.tenantPaymentAlreadyOpen",

  // The cancel-order path. Its handler builds each sentence in hardcoded English, and
  // ORDER_NOT_CANCELLABLE interpolates the raw OrderStatus enum name on top ("...it is already
  // Processing"), so passing them through showed a Vietnamese customer English prose with an
  // internal identifier in it. The status is dropped rather than translated: what the customer
  // can act on is that the order is past cancelling, not which state machine it sits in.
  ORDER_NOT_CANCELLABLE: "errors:payment.orderNotCancellable",

  // Layer 3 of the cancel guard: a machine already exists for this order, so it can no longer be
  // cancelled and the resource has to go first. Its sibling ORDER_ALREADY_PAID (layer 2) is
  // deliberately NOT here — it is already aliased to INVALID_RESOURCE_STATE above, and
  // hasLocalizedWording lets that one pass the backend sentence through on purpose.
  RESOURCE_ALREADY_CREATED: "errors:payment.resourceAlreadyCreated",

  // An order or payment changed while the customer was cancelling it (most often an operator
  // verified the transfer at the same moment). Nothing was written; the page has to be reloaded.
  ORDER_CHANGED: "errors:payment.orderChanged",
  PAYMENT_CHANGED: "errors:payment.paymentChanged",

  // The status guard every payment route shares, and the cancel gate's layer 2. Both are reachable
  // by ordinary clicking — an operator verifying the transfer a moment before the customer cancels
  // or pays — and both sentences are hardcoded English in the handlers. Kept apart because "already
  // paid" and "closed, place a new order" are opposite situations for the customer.
  ORDER_ALREADY_PAID: "errors:payment.orderAlreadyPaid",
  ORDER_NOT_PAYABLE: "errors:payment.orderNotPayable",

  // Cancelling a payment that is no longer open: an operator already rejected or refunded it,
  // confirmed it, or it was cancelled already (another tab, or support). The cancel button is offered
  // while the payment is still AwaitingVerification, so a verify landing first is an ordinary race.
  PAYMENT_ALREADY_CLOSED: "errors:payment.paymentAlreadyClosed",
  PAYMENT_ALREADY_COMPLETED: "errors:payment.paymentAlreadyCompleted",
  PAYMENT_ALREADY_CANCELLED: "errors:payment.paymentAlreadyCancelled",

  // The catch-all CreatePaymentOrderCommandHandler returns for an unexpected failure. Same
  // reasoning: its sentence is deliberately generic, so there is nothing in it worth keeping in
  // English.
  PAYMENT_ORDER_FAILED: "errors:payment.paymentOrderFailed",

  // Returned when a request carries no tenant claim. The client already has wording for the
  // identical AUTHENTICATION_REQUIRED; without this alias the one code that actually reaches a
  // customer fell through to the generic server-error line.
  UNAUTHORIZED: "errors:api.unauthorized",

  // The renewal endpoints. These sentences are hardcoded English in
  // CreateResourceRenewalOrderCommandHandler, and each names something the customer can act on,
  // so they are localized rather than passed through. RENEW_UNAVAILABLE already has an entry
  // further down (errors:vm.renewUnavailable) and is not repeated here. There is deliberately no
  // "renewal order already exists" code: an open order is handed back to be finished, not refused.
  RENEW_CYCLE_UNAVAILABLE: "errors:renewal.cycleUnavailable",

  // The machine's product line was retired from the catalogue, so the pricing engine resolves
  // none and would otherwise quote from the legacy component prices — two to four times the
  // catalogue. The quote refuses instead, and only an operator can clear it (admin console,
  // Product Line Audit), so the sentence has to send the customer to support in their own
  // language rather than leave them re-reading a form that will not price.
  RENEW_PRODUCT_LINE_UNAVAILABLE: "errors:renewal.productLineUnavailable",
  UPGRADE_PRODUCT_LINE_UNAVAILABLE: "errors:upgrade.productLineUnavailable",

  RESOURCE_MISSING_AT_PROVIDER: "errors:renewal.missingAtProvider",

  // Its sibling, and a different fact: the provider was never asked. The row holds no VM uuid at
  // all — handing a machine to another tenant clears the source row's — so there is nothing on the
  // infrastructure for a paid period to keep alive. Kept as its own code rather than folded into
  // the one above so support can tell "we lost the link" apart from "the provider says it is
  // gone".
  RESOURCE_MISSING_PROVIDER_ID: "errors:renewal.missingProviderId",

  // Deliberately NOT listed: RENEWAL_NOT_APPLIED, UPGRADE_NOT_APPLIED and UPGRADE_PARTIALLY_APPLIED.
  // Fulfilment refused (or only partly applied) the renewal or upgrade, and the backend sentence is
  // the only place that says WHY — which disk was deleted, that the machine must be stopped. A key
  // here would outrank it (see hasLocalizedWording) and replace the reason with a generic line, so
  // the message passes through as sent.

  // Shared across every resource endpoint
  AUTHENTICATION_REQUIRED: "errors:api.unauthorized",
  FORBIDDEN: "errors:api.forbidden",
  TENANT_ISOLATION_VIOLATION: "errors:api.forbidden",
  QUOTA_EXCEEDED: "errors:vm.vmQuotaExceeded",

  // Credentials (Access tab)
  CREDENTIALS_NOT_AVAILABLE: "errors:vm.credentialsNotAvailable",
  CREDENTIALS_DECRYPTION_FAILED: "errors:vm.credentialsUnreadable",
  GET_CREDENTIALS_FAILED: "errors:vm.credentialsFetchFailed",

  // Password reset. Every sentence on this path is hardcoded English in the handler, and one of
  // them carries an instruction rather than an apology:
  //
  // PASSWORD_APPLIED_NOT_STORED means the machine already has a password nobody holds, recoverable
  // for exactly as long as it has not rebooted. A customer shown a generic "please try again" would
  // restart the VM to see whether that helps — the one action that makes it unrecoverable. So this
  // wording is translated, and it leads with the reset-before-reboot instruction.
  //
  // PASSWORD_RESET_REFUSED is translated for the opposite reason: the backend passes the provider's
  // own message through verbatim, which is n8n prose the customer cannot act on.
  //
  // CREDENTIALS_UNAVAILABLE is the reset path's name for the situation CREDENTIALS_NOT_AVAILABLE
  // already covers on the credentials endpoint, so it reuses that wording rather than a new string.
  //
  // RESOURCE_NOT_RUNNING is the one the customer can act on by themselves: the provider will not
  // write a password to a machine that is off, so the message says to start it. It belongs to this
  // path alone — no other endpoint returns that code.
  PASSWORD_APPLIED_NOT_STORED: "errors:vm.passwordAppliedNotStored",
  PASSWORD_RESET_REFUSED: "errors:vm.passwordResetRefused",
  RESOURCE_NOT_RUNNING: "errors:vm.passwordResetNeedsRunningVm",
  CREDENTIALS_UNAVAILABLE: "errors:vm.credentialsNotAvailable",

  // Provisioning state and provider identity
  VM_NOT_PROVISIONED: "errors:vm.notProvisioned",
  PROVIDER_ID_MISSING: "errors:vm.notProvisioned",
  METADATA_MISSING: "errors:vm.identifierMissing",
  INVALID_UUID: "errors:vm.identifierMissing",
  INVALID_RESOURCE: "errors:vm.identifierMissing",
  VM_NOT_FOUND: "errors:vm.notFound",
  SIMULATED_RESOURCE: "errors:vm.simulatedResource",
  INVALID_RESOURCE_TYPE: "errors:vm.unsupportedResourceType",
  UNSUPPORTED_RESOURCE_TYPE: "errors:vm.unsupportedResourceType",
  INVALID_ATTACHMENT_TYPE: "errors:vm.invalidAttachmentType",
  WISERVICE_ERROR: "errors:vm.providerError",
  WISERVICE_UPDATE_FAILED: "errors:vm.specsUpdateFailed",
  INVALID_STATE: "errors:vm.invalidState",
  INVALID_RESOURCE_STATUS: "errors:vm.invalidState",
  VM_NOT_STOPPED: "errors:vm.mustBeStopped",

  // Snapshots tab
  CREATE_SNAPSHOT_FAILED: "errors:vm.snapshotCreateFailed",
  DELETE_SNAPSHOT_FAILED: "errors:vm.snapshotDeleteFailed",
  ROLLBACK_SNAPSHOT_FAILED: "errors:vm.snapshotRollbackFailed",
  UPDATE_SNAPSHOT_DESCRIPTION_FAILED: "errors:vm.snapshotUpdateFailed",
  GET_SNAPSHOTS_FAILED: "errors:vm.snapshotsFetchFailed",
  SNAPSHOT_LIMIT_EXCEEDED: "errors:vm.snapshotLimitExceeded",
  SNAPSHOT_NOT_SUPPORTED: "errors:vm.snapshotNotSupported",

  // Storage tab
  DISK_NOT_FOUND: "errors:vm.diskNotFound",
  INVALID_DISK: "errors:vm.diskDeviceNotFound",
  DISK_ALREADY_ATTACHED: "errors:vm.diskAlreadyAttached",
  DISK_ATTACHMENT_FAILED: "errors:vm.diskAttachFailed",
  DISK_DETACHMENT_FAILED: "errors:vm.diskDetachFailed",
  DISK_CREATION_FAILED: "errors:vm.diskCreateFailed",
  DISK_CREATION_AND_ATTACHMENT_FAILED: "errors:vm.diskCreateFailed",
  GET_RESOURCE_DISKS_FAILED: "errors:vm.disksFetchFailed",
  ATTACHMENT_NOT_FOUND: "errors:vm.attachmentNotFound",
  ATTACHMENT_ALREADY_DETACHED: "errors:vm.attachmentAlreadyDetached",
  FETCH_ATTACHMENTS_FAILED: "errors:vm.attachmentsFetchFailed",
  FETCH_PARENT_ATTACHMENTS_FAILED: "errors:vm.attachmentsFetchFailed",

  // Networking tab
  NETWORK_NOT_FOUND: "errors:vm.networkNotFound",
  NETWORK_ALREADY_ATTACHED: "errors:vm.networkAlreadyAttached",
  NETWORK_ATTACHMENT_FAILED: "errors:vm.networkAttachFailed",
  NETWORK_DETACHMENT_FAILED: "errors:vm.networkDetachFailed",
  NETWORK_DELETE_FAILED: "errors:vm.networkDeleteFailed",
  NETWORK_UPDATE_FAILED: "errors:vm.networkUpdateFailed",
  NETWORK_CREATION_FAILED: "errors:vm.networkCreateFailed",
  NETWORK_ATTACHMENT_NOT_FOUND: "errors:vm.networkInterfaceNotFound",

  // Firewall tab
  GET_FIREWALL_RULES_FAILED: "errors:vm.firewallRulesFetchFailed",
  GET_FIREWALL_MACROS_FAILED: "errors:vm.firewallMacrosFetchFailed",
  GET_FIREWALL_LOGS_FAILED: "errors:vm.firewallLogsFetchFailed",

  // Console and metrics
  GET_CONSOLE_FAILED: "errors:vm.consoleFetchFailed",
  CONSOLE_RETRIEVAL_FAILED: "errors:vm.consoleFetchFailed",
  GET_METRICS_FAILED: "errors:vm.metricsFetchFailed",
  METRICS_RETRIEVAL_FAILED: "errors:vm.metricsFetchFailed",

  // Lifecycle: list, detail, resize, renew, delete
  GET_FAILED: "errors:vm.fetchFailed",
  GET_SUMMARY_FAILED: "errors:vm.summaryFetchFailed",
  DELETE_FAILED: "errors:vm.deleteFailed",
  // A machine whose order is unpaid stays deletable; what is refused is deleting one whose RENEWAL
  // transfer is still being verified, because closing that order would cancel a payment the customer
  // may already have made.
  RENEWAL_PAYMENT_IN_REVIEW: "errors:vm.renewalPaymentInReview",
  // Paying an order whose machines the customer has already deleted: the order stays open but stops
  // being payable, so the money is refused before it is sent rather than at fulfilment.
  ORDER_RESOURCES_DELETED: "errors:payment.orderResourcesDeleted",
  UPDATE_FAILED: "errors:vm.updateFailed",
  DOWNGRADE_NOT_ALLOWED: "errors:vm.downgradeNotAllowed",
  NO_CHANGE: "errors:vm.noSpecChange",
  PENDING_UPGRADE_EXISTS: "errors:vm.pendingUpgradeExists",
  CREATE_UPGRADE_FAILED: "errors:vm.upgradeOrderFailed",
  // The upgrade quote's and order's own refusals (UpgradePlanner). Each backend sentence is hardcoded
  // English and carries nothing the key does not: stop the VM, leave the separately billed disk alone,
  // wait for a paid term, the feature is switched off, the machine has no expiry to charge up to.
  // An elapsed term answers RESOURCE_EXPIRED, which already reads "renew first" above.
  VM_MUST_BE_STOPPED: "errors:vm.upgradeNeedsStoppedVm",
  DISK_BILLED_SEPARATELY: "errors:vm.diskBilledSeparately",
  UPGRADE_NOT_ALLOWED_ON_TRIAL: "errors:vm.upgradeNotAllowedOnTrial",
  VM_UPGRADE_DISABLED: "errors:vm.upgradeDisabled",
  UPGRADE_TERM_UNKNOWN: "errors:vm.upgradeTermUnknown",
  // A target past the range the machine's product line sells. The wizard draws its options from that
  // range, so this is only met when the range moved while the wizard was open.
  UPGRADE_SPEC_OUT_OF_RANGE: "errors:vm.upgradeSpecOutOfRange",
  // Refused before a QR bill or a transfer is opened for an upgrade order (UpgradeBlock): the
  // machine was renewed after the order was priced, so the customer has to start the upgrade again.
  UPGRADE_PRICE_OUTDATED: "errors:vm.upgradePriceOutdated",
  DUPLICATE_RESOURCE_NAME: "errors:vm.duplicateName",
  // Refused by the order and the trial alike when the machine is smaller than its OS image needs.
  // The backend sentence lists the shortfalls in English; the wizard blocks the same case earlier
  // with the numbers localized, so this shows when that check was bypassed or read minimums that
  // have since been raised (the draft keeps the OS it was started with).
  BELOW_TEMPLATE_MINIMUM_SPECS: "errors:vm.belowOsMinimum",
  RENEW_UNAVAILABLE: "errors:vm.renewUnavailable",
  RENEW_CONFLICT: "errors:vm.renewConflict",
  RENEWAL_IN_PROGRESS: "errors:vm.renewInProgress",
  BULK_START_FAILED: "errors:vm.bulkActionFailed",
  BULK_STOP_FAILED: "errors:vm.bulkActionFailed",
  BULK_DELETE_FAILED: "errors:vm.bulkActionFailed",

  // Delete/reinstall (DeleteResourceCommandHandler, ResourceReinstallService). Every sentence on
  // this path is hardcoded English in the handler, and each names something the customer can act
  // on — wait, reload, or nothing at all.
  RESOURCE_REINSTALLING: "errors:vm.reinstalling",
  REINSTALL_NOT_AVAILABLE: "errors:vm.reinstallNotAvailable",
  REINSTALL_CONFLICT: "errors:vm.reinstallConflict",
  RESTORE_WINDOW_EXPIRED: "errors:vm.restoreWindowExpired",
  RESOURCE_NAME_TAKEN: "errors:vm.restoreNameTaken",
  RESTORE_NOT_AVAILABLE: "errors:vm.restoreNotAvailable",
  RESTORE_CONFLICT: "errors:vm.restoreConflict",
  TEMPLATE_NOT_FOUND: "errors:vm.templateNotFound",
  TEMPLATE_INACTIVE: "errors:vm.templateInactive",
  TEMPLATE_NAME_MISSING: "errors:vm.templateNameMissing",

  // The in-place reinstall's own gate (design doc §5a), a live VM only. Reached only on a stale
  // form or a race — ReinstallOSDialog's own gate and its typed-name/reason fields already stop the
  // ordinary cases, so these exist for defense in depth rather than as the primary UI.
  RESOURCE_NOT_STOPPED: "errors:vm.reinstallNotStopped",
  REINSTALL_REASON_REQUIRED: "errors:vm.reinstallReasonRequired",
  CONFIRMATION_NAME_MISMATCH: "errors:vm.confirmationNameMismatch",
  // Shared with the delete gate's own suspended refusal (design doc §4 step 1) — reinstall's wording
  // is the more specific of the two ("resume, then stop, then act"), and delete's own dialog already
  // stops the ordinary case client-side before it reaches this code.
  RESOURCE_SUSPENDED: "errors:vm.reinstallSuspended",
  RESOURCE_BUSY: "errors:vm.reinstallBusy",
  RESOURCE_SPEC_MISMATCH: "errors:vm.specMismatch",

  // The delete's provider check, run before anything is written (VmDeletionService). Each one
  // leaves the VM unchanged; the English sentences name WiService internals, so they are localized.
  // Only the last two are worth retrying — the first two need support.
  VM_UUID_MISSING: "errors:vm.uuidMissing",
  VM_NOT_FOUND_ON_PROVIDER: "errors:vm.notFoundOnProvider",
  PROVIDER_UNAVAILABLE: "errors:vm.providerUnavailable",
  DELETE_NOT_STARTED: "errors:vm.deleteNotStarted",
  DELETE_CONFLICT: "errors:vm.deleteConflict",

  // The `vm_deletion_enabled` / `vm_suspend_enabled` platform flags, enforced by the API as well as
  // hidden by the console; the second covers Suspend and Resume alike. Reached only when a flag is
  // switched off under an open page: the console's copy of the flags is up to 5 minutes old.
  VM_DELETION_DISABLED: "errors:vm.deletionDisabled",
  VM_SUSPEND_DISABLED: "errors:vm.suspendDisabled",

  // Renewal quote, renewal order and pay-with-balance refuse a VM whose deletion is under way
  // (Terminating); a reinstall in flight answers RESOURCE_REINSTALLING above, worded for both paths.
  RESOURCE_BEING_DELETED: "errors:vm.beingDeleted",

  // Semantic not-found shared by every resource endpoint. Mapped so the English "Resource not
  // found." does not reach a Vietnamese console; the numbered RES_1301 keeps its generic wording.
  RESOURCE_NOT_FOUND: "errors:vm.resourceNotFound",

  // Retry provisioning (2026-09-28). Offered only when the batch check endpoint
  // (`checkProvisioningRetry` / `useProvisioningRetryCheck`) answers `allowed: true`, so every one
  // of these is a race since that check ran (the order was refunded, the window closed) rather
  // than the ordinary case. Every backend sentence on this path is hardcoded English, so all of
  // them are localized.
  RESOURCE_NOT_A_VM: "errors:vm.retryNotAVm",
  RESOURCE_STATUS_NOT_RETRYABLE: "errors:vm.retryStatusNotRetryable",
  RESOURCE_ALREADY_PROVISIONED: "errors:vm.retryAlreadyProvisioned",
  NOT_A_PROVISIONING_FAILURE: "errors:vm.retryNotAProvisioningFailure",
  ORDER_REQUIRED: "errors:vm.retryOrderRequired",
  ORDER_NOT_ELIGIBLE: "errors:vm.retryOrderNotEligible",
  ORDER_NOT_PAID: "errors:vm.retryOrderNotPaid",
  ORDER_REFUNDED: "errors:vm.retryOrderRefunded",
  BILLING_CYCLE_NOT_USABLE: "errors:vm.retryBillingCycleNotUsable",
  PROVISIONING_IN_PROGRESS: "errors:vm.retryInProgress",
  PROVISIONING_RETRY_CONFLICT: "errors:vm.retryConflict",
  PROVISIONING_OUTCOME_UNKNOWN: "errors:vm.retryOutcomeUnknown",
  // Only within 24h of the VM's creation, enforced by the server on both consoles.
  RETRY_WINDOW_EXPIRED: "errors:vm.retryWindowExpired",
};

/**
 * Tells an error code apart from display text. Codes are SCREAMING_SNAKE_CASE
 * (`AUTH_1001`, `INSUFFICIENT_BALANCE`); a localized sentence never is.
 *
 * Matching on shape rather than against a fixed list matters: a code the frontend has
 * not mapped yet still degrades to the generic server message instead of showing the
 * user a raw token like `AUTH_9999`.
 */
const ERROR_CODE_PATTERN = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*$/;

/**
 * HTTP status code to error code mapping
 */
export const httpStatusToErrorCode: Record<number, string> = {
  400: ErrorCode.VALIDATION_FAILED,
  401: ErrorCode.TOKEN_EXPIRED,
  403: ErrorCode.PERMISSION_DENIED,
  404: ErrorCode.RESOURCE_NOT_FOUND,
  408: ErrorCode.REQUEST_TIMEOUT,
  409: ErrorCode.RESOURCE_ALREADY_EXISTS,
  422: ErrorCode.VALIDATION_FAILED,
  429: ErrorCode.RATE_LIMIT_EXCEEDED,
  500: ErrorCode.INTERNAL_SERVER_ERROR,
  502: ErrorCode.SERVICE_UNAVAILABLE,
  503: ErrorCode.SERVICE_UNAVAILABLE,
  504: ErrorCode.REQUEST_TIMEOUT,
};

export type ErrorSeverity = "error" | "warning" | "info";

export interface ResolveErrorOptions {
  /** Message to use when nothing more specific can be extracted from the source. */
  fallback?: string;
  /** Show a toast using the resolved severity. */
  toast?: boolean;
  /** Log to the console in dev and to the server log endpoint in production. */
  log?: boolean;
  /** Extra fields attached to the log entry. Ignored unless `log` is set. */
  context?: Record<string, unknown>;
}

export interface ResolvedError {
  /** Best human-readable message for this failure. Always populated. */
  message: string;
  /** Short heading, only when the source carried one. */
  title?: string;
  /** Suggested next step, only when the source carried one. */
  action?: string;
  severity: ErrorSeverity;
  /** Error code reported by the API, when present. */
  code?: string;
  /** HTTP status, when the failure came from a response. */
  status?: number;
  /** Field-level validation errors keyed by field name, when the backend returned them. */
  fields?: Record<string, string>;
  isNetwork: boolean;
  isAuth: boolean;
  isPermission: boolean;
  isValidation: boolean;
}

/** Shape a caller can hand in when it has already composed the wording itself. */
export interface ComposedError {
  message: string;
  title?: string;
  action?: string;
  severity?: ErrorSeverity;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function translate(key: string): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic translation keys
  const result = i18n.t(key as any);
  return typeof result === "string" ? result : String(result);
}

/** Resolves an error code — numbered or semantic — to a localized message. */
function translateErrorCode(errorCode?: string): string {
  const canonical = errorCode
    ? (ERROR_CODE_ALIASES[errorCode] ?? errorCode)
    : ErrorCode.UNKNOWN_ERROR;
  return translate(
    ERROR_CODE_TRANSLATION_KEYS[canonical] ??
      (errorCode ? VM_ERROR_TRANSLATION_KEYS[errorCode] : undefined) ??
      "errors:api.serverError",
  );
}

/**
 * Whether the frontend has wording of its own for this code, and should therefore prefer
 * it over the English sentence the backend attached to the same response.
 */
function hasLocalizedWording(errorCode: string): boolean {
  const aliased = ERROR_CODE_ALIASES[errorCode];
  if (aliased) {
    // PAYMENT_ALREADY_VERIFIED lands on INVALID_RESOURCE_STATE, whose generic "conflicts with
    // existing data" says less than the backend's own sentence.
    return aliased !== ErrorCode.INVALID_RESOURCE_STATE;
  }

  return errorCode in VM_ERROR_TRANSLATION_KEYS;
}

/**
 * Get error message from HTTP status code
 */
export const getErrorMessageFromStatus = (statusCode: number): string =>
  translateErrorCode(httpStatusToErrorCode[statusCode]);

/**
 * Pulls field-level validation errors out of the two shapes the backend emits:
 * the FluentValidation middleware (`error.details.errors: { Field: [...] }`) and
 * ASP.NET model state (`error.details: [{ field, errors: [...] }]`).
 */
function extractFieldErrors(
  data: Record<string, unknown> | undefined,
): Record<string, string> | undefined {
  if (!data) return undefined;

  const apiError = isRecord(data.error) ? data.error : undefined;
  const details = apiError?.details;
  const fields: Record<string, string> = {};

  if (Array.isArray(details)) {
    for (const detail of details) {
      if (!isRecord(detail) || !Array.isArray(detail.errors)) continue;
      const message = detail.errors.find(
        (value): value is string => typeof value === "string",
      );
      const field = typeof detail.field === "string" ? detail.field : undefined;
      if (field && message) fields[field] = message;
    }
  }

  const detailsObject = isRecord(details) ? details : undefined;
  const rawFields = isRecord(detailsObject?.errors)
    ? detailsObject.errors
    : isRecord(data.errors)
      ? data.errors
      : undefined;

  if (rawFields) {
    for (const [field, value] of Object.entries(rawFields)) {
      if (Array.isArray(value) && value.length > 0) {
        fields[field] = String(value[0]);
      } else if (typeof value === "string") {
        fields[field] = value;
      }
    }
  }

  return Object.keys(fields).length > 0 ? fields : undefined;
}

function logFailure(
  result: ResolvedError,
  context?: Record<string, unknown>,
): void {
  if (import.meta.env.DEV) {
    console.error("[Error]", result, context ?? {});
    return;
  }

  if (!import.meta.env.PROD) return;

  void fetch("/api/v1/logs/errors", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message: result.message,
      code: result.code,
      status: result.status,
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    }),
  }).catch(() => {
    // Reporting a failure must never create another one.
  });
}

function emit(
  result: ResolvedError,
  options: ResolveErrorOptions,
  source?: unknown,
): ResolvedError {
  if (options.log) {
    logFailure(result, options.context);
  }

  if (options.toast) {
    const notify =
      result.severity === "warning"
        ? toast.warning
        : result.severity === "info"
          ? toast.info
          : toast.error;

    const description = [
      result.title ? result.message : undefined,
      result.action,
    ]
      .filter(Boolean)
      .join("\n\n");

    notify(result.title ?? result.message, {
      description: description || undefined,
      closeButton: true,
      // A request failure has usually been toasted by the api client already. Reusing that toast's
      // id replaces it with this one rather than showing the same failure twice.
      id: apiErrorToastId(source),
    });
  }

  return result;
}

const NEUTRAL_FLAGS = {
  isNetwork: false,
  isAuth: false,
  isPermission: false,
  isValidation: false,
} as const;

/**
 * Normalizes any failure into a {@link ResolvedError}.
 *
 * @param source A known error code, a ready-made message, a {@link ComposedError},
 *   an Axios/HTTP error, an `Error`, or anything else.
 * @param options Fallback message plus optional toast/log side effects.
 */
export function resolveError(
  source: unknown,
  options: ResolveErrorOptions = {},
): ResolvedError {
  if (source === undefined || source === null) {
    return emit(
      {
        ...NEUTRAL_FLAGS,
        message: options.fallback ?? translateErrorCode(),
        severity: "error",
      },
      options,
    );
  }

  if (typeof source === "string") {
    // A code gets localized; anything else is already display text and is handed
    // straight back, so callers can pass a `t(...)` string through unchanged.
    return ERROR_CODE_PATTERN.test(source)
      ? emit(
          {
            ...NEUTRAL_FLAGS,
            message: translateErrorCode(source),
            severity: "error",
            code: source,
          },
          options,
        )
      : emit({ ...NEUTRAL_FLAGS, message: source, severity: "error" }, options);
  }

  // An already-composed failure (title/message/action) passes through untouched.
  if (
    isRecord(source) &&
    typeof source.message === "string" &&
    !("response" in source) &&
    !(source instanceof Error)
  ) {
    return emit(
      {
        ...NEUTRAL_FLAGS,
        message: source.message,
        title: typeof source.title === "string" ? source.title : undefined,
        action: typeof source.action === "string" ? source.action : undefined,
        severity: (source.severity as ErrorSeverity | undefined) ?? "error",
      },
      options,
    );
  }

  // HTTP failure: dig through the response envelope.
  if (isRecord(source) && "response" in source) {
    const response = isRecord(source.response) ? source.response : undefined;
    const data = isRecord(response?.data) ? response.data : undefined;
    const apiError = isRecord(data?.error) ? data.error : undefined;
    const status =
      typeof response?.status === "number" ? response.status : undefined;
    const requestCode =
      typeof source.code === "string" ? source.code : undefined;

    const code =
      (typeof apiError?.code === "string" ? apiError.code : undefined) ??
      (typeof data?.code === "string" ? data.code : undefined) ??
      (typeof data?.errorCode === "string" ? data.errorCode : undefined);

    const fields = extractFieldErrors(data);

    // Most specific wins: a field rule beats domain code translation, envelope summary, or status fallback.
    // The envelope summary sits below the code translation because it is hardcoded English in the
    // handlers — see hasLocalizedWording for which codes we override it for.
    const message =
      (fields ? Object.values(fields)[0] : undefined) ??
      (code && hasLocalizedWording(code)
        ? translateErrorCode(code)
        : undefined) ??
      (typeof apiError?.message === "string" && apiError.message
        ? apiError.message
        : undefined) ??
      (typeof data?.message === "string" && data.message
        ? data.message
        : undefined) ??
      (typeof data?.errorMessage === "string" && data.errorMessage
        ? data.errorMessage
        : undefined) ??
      (code ? translateErrorCode(code) : undefined) ??
      (status ? getErrorMessageFromStatus(status) : undefined) ??
      options.fallback ??
      translateErrorCode();

    return emit(
      {
        ...NEUTRAL_FLAGS,
        message,
        severity: status === 401 ? "warning" : "error",
        code,
        status,
        fields,
        isNetwork:
          requestCode === "ERR_NETWORK" || requestCode === "ECONNABORTED",
        isAuth: status === 401,
        isPermission: status === 403,
        isValidation: status === 400 || status === 422,
      },
      options,
      source,
    );
  }

  if (source instanceof Error) {
    // Axios rejects network/timeout failures with no `response` at all, so the flag has
    // to come from the request code here rather than from a status.
    const rawCode: unknown = (source as unknown as Record<string, unknown>)
      .code;
    const requestCode = typeof rawCode === "string" ? rawCode : undefined;

    return emit(
      {
        ...NEUTRAL_FLAGS,
        message: source.message || options.fallback || translateErrorCode(),
        severity: "error",
        code: requestCode,
        isNetwork:
          requestCode === "ERR_NETWORK" || requestCode === "ECONNABORTED",
      },
      options,
      source,
    );
  }

  return emit(
    {
      ...NEUTRAL_FLAGS,
      message: options.fallback ?? translateErrorCode(),
      severity: "error",
    },
    options,
  );
}

/**
 * Resolves any failure down to the message to show the user.
 * Equivalent to `resolveError(source, options).message`.
 */
export const getErrorMessage = (
  source?: unknown,
  options?: ResolveErrorOptions,
): string => resolveError(source, options).message;

/** The two refusals that mean "another payment is still open", answered by `AwaitingPaymentDialog`. */
export type OpenPaymentRefusal =
  | "TENANT_PAYMENT_ALREADY_OPEN"
  | "ORDER_PAYMENT_ALREADY_OPEN";

/**
 * The open-payment refusal a failed payment carries, or null for any other failure. The screens
 * that create a payment answer it with `AwaitingPaymentDialog` instead of a toast.
 */
export const getOpenPaymentRefusal = (
  source: unknown,
): OpenPaymentRefusal | null => {
  const code = resolveError(source).code;
  return code === "TENANT_PAYMENT_ALREADY_OPEN" ||
    code === "ORDER_PAYMENT_ALREADY_OPEN"
    ? code
    : null;
};
